import React, { useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { ICON_BUTTON_SIZE, ICON_BUTTON_TYPE } from '../../global/constants';
import { useAccountingData } from '../../hooks/accounting/useAccountingData';
import { useAccountingModals } from '../../hooks/accounting/useAccountingModals';
import { useMobileDevice } from '../../global/useMobile';
import { AccountingProps } from '../../interfaces/ProjectInterface';
import { rootStore } from '../../mobx/rootStore';
import IconButton from '../button/iconButton';
import AccountingHeader from './components/AccountingHeader';
import AccountingModals from './components/AccountingModals';
import FileFilterDropdownContent from './components/FileFilterDropdownContent';
import TaskFilterBar from './components/TaskFilterBar';
import TaskFilterDropdownContent from './components/TaskFilterDropdownContent';
import { Container, MainContent } from './styles';

const Accounting: React.FC<AccountingProps> = (props) => {
  const accounting = useAccountingData(props);
  const modalState = useAccountingModals();
  const isMobile = useMobileDevice();
  const { currentProject, view } = accounting;
  const { openModal } = modalState;

  const renderTaskFilterIcon = useCallback(
    () => (
      <IconButton
        iconName='filters-icon'
        size={isMobile ? ICON_BUTTON_SIZE.LARGE : ICON_BUTTON_SIZE.MEDIUM}
        variant={ICON_BUTTON_TYPE.TERTIARY}
      />
    ),
    [isMobile]
  );

  const createTaskModal = useCallback(() => {
    if (!currentProject?.id) return;
    rootStore.taskStore.createTaskInput.setProject(currentProject.id);
    openModal('task');
  }, [currentProject?.id, openModal]);

  const openEditProjectModal = useCallback(() => {
    if (!currentProject) return;
    rootStore.projectStore.createProjectInput.updateWith(currentProject);
    openModal('project');
  }, [currentProject, openModal]);

  const taskFilterContent = useMemo(() => <TaskFilterDropdownContent accounting={accounting} />, [accounting]);
  const fileFilterContent = useMemo(() => <FileFilterDropdownContent accounting={accounting} />, [accounting]);

  const headerProps = {
    currentProject,
    currentUser: accounting.currentUser,
    currentWorkspace: accounting.currentWorkspace,
    history: accounting.history,
    isTaskFilterOpen: accounting.isTaskFilterOpen ?? false,
    params: accounting.params,
    view,
    isMobile,
    mobileFilterContent: view.isFile ? fileFilterContent : taskFilterContent,
    onArchive: () => openModal('archive'),
    onEditProject: openEditProjectModal,
    renderTaskFilterIcon
  };

  return (
    <>
      {/* this is for responsive Header  */}
      {isMobile && <AccountingHeader {...headerProps} placement='mobile' />}
      <MainContent>
        <Container>
          <AccountingHeader {...headerProps} placement='desktop' />
          <TaskFilterBar
            isTaskFilterOpen={accounting.isTaskFilterOpen ?? false}
            view={view}
            isMobile={isMobile}
            renderDropdownItems={props.renderDropdownItems}
            taskFilterContent={taskFilterContent}
            fileFilterContent={fileFilterContent}
            onCreateTask={createTaskModal}
            onCreateMilestone={() => openModal('milestone')}
          />
        </Container>
      </MainContent>
      <AccountingModals
        archiveProject={accounting.archiveProject}
        createProjectInput={accounting.createProjectInput}
        loadData={accounting.loadData}
        loading={accounting.loading}
        params={accounting.params}
        modalState={modalState}
        loadTaskList={props.loadTaskList}
      />
    </>
  );
};

export default observer(Accounting);
